import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AnyAction } from 'redux'
import { ActionType } from 'typesafe-actions'
import { THttpErrorAction } from '../apiState/types'
import { NAMESPACE } from './constants'
import {
  TAllocateSuspenseAccountRequest,
  TSaveSuspenseNotePayload,
  TSaveSuspenseNoteResult,
  TBulkWalletTransferPayload,
  TDepositTransferPayload,
  TDepositTransferResult,
  TPartyBalances,
  TPartyParams,
  TPartyRequest,
  TPaymentHistory,
  TPaymentHistoryRequest,
  TPaymentRow,
  TPayoutRequest,
  TPayoutResult,
  TPopRequest,
  TRefundRequest,
  TRefundResult,
  TReferenceLookup,
  TSupportedBanks,
  TSuspenseAccount,
  TWalletState,
  TWalletTransaction,
  TWalletTransferRequest,
} from './types'

const initialState: TWalletState = {
  balances: {},
  paymentHistory: {
    payments: [],
    // `PaymentHistoryResponse.period` is `{ from, to }`; the only reader
    // (`ui/payments.getPaymentPeriod`) already defaults a miss to `{}`.
    period: {},
  },
  approvedPayments: [],
  referenceLookups: {}, // { 'somereference': {....} }
  unallocatedSuspenseAccounts: [],
  allocatedSuspenseAccounts: [],
  isWithdrawalModalOpen: false,
  loading: {
    pop_request: false,
  },
}

/**
 * NB the slice is named `wallet`, not `` `${NAMESPACE}Api` ``, so its action types are
 * `wallet/*`. Left as-is: renaming would change every action-type string.
 */
const walletSlice = createSlice({
  name: NAMESPACE,
  initialState,
  reducers: {
    balance_request: (state, { payload }: PayloadAction<TPartyParams>) => {},
    balance_success: {
      reducer: (state, { payload, meta }: PayloadAction<TPartyBalances, string, TPartyParams>) => {
        state.balances[meta.partyId] = payload
      },
      prepare: (payload: TPartyBalances, meta: TPartyParams) => ({ payload, meta, error: null }),
    },
    payout_request: (state, { payload }: PayloadAction<TPayoutRequest>) => {},
    payout_success: (state, { payload }: PayloadAction<TPayoutResult>) => {
      const { amount, party, asset } = payload
      const globalBalance = state.balances[party]?.globalBalance
      /**
       * Optimistically deduct the payout from the cached balance. Guarded: with no balance
       * loaded for the party this used to throw inside the reducer on `.globalBalance.assets`.
       */
      if (globalBalance) {
        globalBalance.assets = globalBalance.assets.map(a =>
          a.name === asset ? { ...a, balance: a.balance - amount } : a,
        )
      }
      state.isWithdrawalModalOpen = false
    },
    payments_request: (state, { payload }: PayloadAction<TPaymentHistoryRequest>) => {},
    payments_success: (state, { payload }: PayloadAction<TPaymentHistory>) => {
      state.paymentHistory = payload
    },
    approvedPayments_success: (state, { payload }: PayloadAction<TPaymentRow[]>) => {
      state.approvedPayments = payload
    },
    reversePayments_request: (state, { payload }: PayloadAction<string[]>) => {
      state.paymentHistory.payments = state.paymentHistory.payments.map(p => ({
        ...p,
        animateDanger: payload.includes(p.payment),
      }))
    },
    reversePayments_success: (state, { payload }: PayloadAction<string[]>) => {
      state.paymentHistory.payments
        .filter(p => payload.includes(p.payment))
        .forEach(payment => {
          payment.paymentStatus = 'Reversed'
        })
    },
    demandPayments_request: (state, { payload }: PayloadAction<string[]>) => {
      state.paymentHistory.payments = state.paymentHistory.payments.map(p => ({
        ...p,
        animateSuccess: payload.includes(p.payment),
      }))
    },
    demandPayments_success: (state, { payload }: PayloadAction<string[]>) => {
      state.paymentHistory.payments
        .filter(p => payload.includes(p.payment))
        .forEach(payment => {
          payment.paymentStatus = 'Demanded'
        })
    },
    /** @todo rename deposit transfer */
    transfer_request: (state, { payload }: PayloadAction<TDepositTransferPayload>) => {},
    transfer_success: (state, { payload }: PayloadAction<TDepositTransferResult>) => {
      const { meta } = payload
      if (meta.partyId && meta.account && meta.amount) {
        /**
         * Optimistically deduct from the Trust account. Guarded: with the balance not
         * loaded, or no matching Trust account, `.find(...).assets` used to throw.
         */
        const trustAsset = state.balances[meta.partyId]?.accountBalances.find(
          b => b.account === meta.account && b.accountType === 'Trust',
        )?.assets[0]
        if (trustAsset) {
          trustAsset.balance -= meta.amount
        }
      }
    },
    walletTransfer_request: (state, { payload }: PayloadAction<TWalletTransferRequest>) => {},
    walletTransfer_success: (state, { payload }: PayloadAction<TWalletTransaction>) => {},
    bulkWalletTransfer_request: (state, { payload }: PayloadAction<TBulkWalletTransferPayload>) => {},
    bulkWalletTransfer_success: (state, { payload }: PayloadAction<{ csvBase64: string }>) => {},
    refund_request: (state, { payload }: PayloadAction<TPartyRequest<TRefundRequest>>) => {},
    refund_success: (state, { payload }: PayloadAction<TRefundResult>) => {},
    withdraw_request: (state, { payload }: PayloadAction<TPartyRequest<TPayoutRequest>>) => {},
    withdraw_success: (state, { payload }: PayloadAction<TWalletTransaction>) => {},
    consolidatedWithdraw_request: (state, { payload }: PayloadAction<TPartyRequest<TPayoutRequest>>) => {},
    consolidatedWithdraw_success: (state, { payload }: PayloadAction<TWalletTransaction>) => {},
    pop_request: (state, { payload }: PayloadAction<TPopRequest>) => {
      state.loading.pop_request = true
    },
    pop_success: (state, { payload }: PayloadAction<{ request: string }>) => {
      state.loading.pop_request = false
    },
    /**
     * Dispatched directly by `wallets/walletTransfer`'s lookup epic, which posts to
     * CREATE_WALLET itself. The `walletRef` meta is not read by any reducer.
     */
    createWallet_success: {
      reducer: (state, { payload }: PayloadAction<TWalletTransaction, string, { walletRef?: string }>) => {},
      prepare: (payload: TWalletTransaction, meta: { walletRef?: string } = {}) => ({ payload, meta, error: null }),
    },
    unallocatedSuspenseAccountss_request: () => {},
    unallocatedSuspenseAccountss_success: (state, { payload }: PayloadAction<TSuspenseAccount[]>) => {
      state.unallocatedSuspenseAccounts = payload
    },
    allocatedSuspenseAccounts_request: () => {},
    allocatedSuspenseAccounts_success: (state, { payload }: PayloadAction<TSuspenseAccount[]>) => {
      state.allocatedSuspenseAccounts = payload
    },
    allocateSuspenseAccount_request: (
      state,
      { payload }: PayloadAction<{ body: TAllocateSuspenseAccountRequest; params: { txId: string } }>,
    ) => {},
    allocateSuspenseAccount_success: {
      reducer: (state, { meta }: PayloadAction<unknown, string, { txId?: string }>) => {
        if (meta?.txId) {
          state.unallocatedSuspenseAccounts = state.unallocatedSuspenseAccounts.filter(ref => ref.txId !== meta.txId)
        }
      },
      prepare: (payload: unknown, meta: { txId?: string }) => ({ payload, meta, error: null }),
    },
    saveSuspenseNote_request: (state, { payload }: PayloadAction<TSaveSuspenseNotePayload>) => {},
    /** Carries the saved note, as the API only answers "Done" and its read side lags. */
    saveSuspenseNote_success: (state, { payload }: PayloadAction<TSaveSuspenseNoteResult>) => {},
    referenceLookup_request: (state, { payload }: PayloadAction<{ reference: string }>) => {},
    /**
     * `ReferenceLookupResponse.result` is optional — the epic passes it straight through,
     * so a reference that resolves to nothing arrives here as `undefined`.
     */
    referenceLookup_success: (state, { payload }: PayloadAction<TReferenceLookup | undefined>) => {
      if (payload?.reference) {
        state.referenceLookups[payload.reference] = payload
      }
    },
    banks_request: () => {},
    banks_success: (state, { payload }: PayloadAction<TSupportedBanks>) => {
      state.banks = payload
    },
    /**
     * @todo: move this to ui module
     */
    withdrawalModalRequestOpened: state => {
      state.isWithdrawalModalOpen = true
    },
    withdrawalModalRequestClosed: state => {
      state.isWithdrawalModalOpen = false
    },
  },
  extraReducers: builder => {
    /**
     * `pop_request` keeps its own loading flag rather than using apiState's, and only
     * `pop_success` cleared it — so after a failed request the Send POP dialog showed a
     * spinner in place of its form until the page was reloaded.
     */
    builder.addMatcher(isPopRequestError, state => {
      state.loading.pop_request = false
    })
  },
})

function isPopRequestError(action: AnyAction): action is THttpErrorAction {
  return action.type === 'http_error' && action.meta?.initAction?.type === `${NAMESPACE}/pop_request`
}

const { reducer, actions: events } = walletSlice

export type WalletEvent = ActionType<typeof events>

export { events, initialState, reducer }
